import { hashKey, onlineManager, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import type { Place, WeatherReport } from '../../shared/contract'
import { qualityOf } from '../../edge/quality'
import { IS_MOCK, MOCK_DELAY_MS } from '../mockMode'
import { allPlaces, selectedPlace, useApp } from '../store'
import { loadEngineModule } from './engineLoader'
import { INSTANT_MAX_AGE_MS, OFFLINE_MAX_AGE_MS, pruneReports, readReports, reportKey, saveReport } from './lastReports'

/**
 * App start-up for speed (see main.tsx):
 *  1. the engine Web Worker boots before React renders (index.html's boot script usually
 *     started it already, with the selected place's report building);
 *  2. the last complete report of every saved place (≤ 6 h old) is painted immediately from
 *     IndexedDB while the engine refreshes in the background (the stale badge shows its age);
 *  3. saved places prefetch in parallel at idle after the first frame, independently
 *     of the selected report; data-saver users skip speculative prefetch;
 *  4. every complete report is saved again for the next open.
 * Performance marks (`wf:report-cached|early|full`) feed scripts/perf/browser-perf.py.
 */

declare global { interface Window { __wfDeferEngine?: boolean } }
export const warmPaintPending = () => typeof window !== 'undefined' && window.__wfDeferEngine === true

type LatLon = Pick<Place, 'latitude' | 'longitude'>
type OptionsFor = (p: LatLon) => { queryKey: QueryKey }

/** Other places start refreshing after this even if the selected place is still loading. */
const GATE_TIMEOUT_MS = 6000

// ─── Early / seeded reports (client-side marks; never on the wire) ────────────

const earlyReports = new WeakSet<WeatherReport>()
const seededReports = new WeakSet<WeatherReport>()

export function markComplete(r: WeatherReport | undefined): void { if (r) earlyReports.delete(r) }

export function markEarly(r: WeatherReport): WeatherReport {
  earlyReports.add(r)
  return r
}
/** Sources the engine hasn't heard from yet read "Still loading…" in an early report. */
const hasLoadingSources = (r: WeatherReport) => r.sources.some((s) => /^still loading/i.test(s.detail))
/**
 * A first-screen report streamed ahead of the complete one (some sources still loading).
 * Identity alone isn't enough: React Query's structural sharing can hand back a copy.
 */
export const isEarlyReport = (r: WeatherReport | undefined) => !!r && (earlyReports.has(r) || hasLoadingSources(r))
/**
 * A report the page may show as final: the complete one, or a streamed one that already carries the
 * whole roster (an edge snapshot of full tier). A lite or partial report is not: a better one is on its way.
 */
export const isSettledReport = (r: WeatherReport | undefined) => !!r && !hasLoadingSources(r) && (!earlyReports.has(r) || qualityOf(r).tier === 'full')

// ─── Selected-first gate ──────────────────────────────────────────────────────

let gateOpen = true
const gateListeners = new Set<() => void>()
function setGate(open: boolean) {
  if (gateOpen === open) return
  gateOpen = open
  for (const l of gateListeners) l()
}
/** False while the selected place's first report is loading at start-up (others wait). */
export function useBootGate(): boolean {
  return useSyncExternalStore(
    (cb) => {
      gateListeners.add(cb)
      return () => gateListeners.delete(cb)
    },
    () => gateOpen,
    () => true,
  )
}

// ─── helpers ─────────────────────────────────────────────────────────────────

const marked = new Set<string>()
function mark(name: string) {
  if (marked.has(name)) return
  marked.add(name)
  try {
    performance.mark(name)
  } catch {
    /* no User Timing */
  }
}

function idle(fn: () => void, timeout = 2000) {
  const w = globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout })
  else setTimeout(fn, 300)
}

const savedKeys = () => new Set(allPlaces(useApp.getState()).map((p) => reportKey(p.latitude, p.longitude)))

/** Starts the engine worker now (idempotent); the data layer reuses it. */
export function startEngine(): void {
  if (IS_MOCK || warmPaintPending()) return
  void loadEngineModule()
    .then((m) => (m as { bootEngine?: () => void }).bootEngine?.())
    .catch(() => undefined)
}

let booted = false

/**
 * Seeds the query cache from the saved reports and wires persistence, marks, the
 * selected-first gate and the idle prefetch. Resolves once the saved reports are in the
 * cache (the caller renders then, or after a short cap).
 */
export async function bootData(client: QueryClient, optionsFor: OptionsFor): Promise<void> {
  if (booted) return
  booted = true
  // React Query assumes "online" until it sees an offline event; opened without a network,
  // no such event comes. Start from the browser's answer so queries pause (the saved report
  // shows, labelled offline) and resume — refetching at once — on the `online` event.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) onlineManager.setOnline(false)
  watch(client, optionsFor)
  if (IS_MOCK) {
    if (MOCK_DELAY_MS) return // a delayed demo paints its skeleton first (mockMode.ts)
    // Demo data has no saved copy, but the first frame still paints weather (not a skeleton that fills in a moment later):
    // the synthetic reports are built in a few ms once their chunk is in, so every place is in the cache before React renders.
    const places = allPlaces(useApp.getState())
    await Promise.allSettled(places.map((p) => client.prefetchQuery(optionsFor(p) as Parameters<QueryClient['prefetchQuery']>[0])))
    return
  }
  setGate(false)
  setTimeout(() => setGate(true), GATE_TIMEOUT_MS)
  const state = useApp.getState()
  const places = allPlaces(state)
  const selected = selectedPlace(state)
  // Offline, an older report (clearly labelled with its time) beats an endless skeleton.
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  const saved = await readReports(
    places.map((p) => reportKey(p.latitude, p.longitude)),
    offline ? OFFLINE_MAX_AGE_MS : INSTANT_MAX_AGE_MS,
  )
  for (const p of places) {
    const report = saved.get(reportKey(p.latitude, p.longitude))
    const { queryKey } = optionsFor(p)
    if (!report || client.getQueryData(queryKey)) continue
    seededReports.add(report)
    // Stamped with its real age: React Query refetches it right away, the badge shows the age.
    client.setQueryData(queryKey, report, { updatedAt: Date.parse(report.generated_at) })
    if (p.id === selected.id) mark('wf:report-cached')
  }
  idle(() => void pruneReports(savedKeys()), 10_000)
}

let prefetchOnPaint = () => {}
/** Called after React commits its first screen, not while hydration is still waiting. */
export function prefetchAfterPaint() {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (warmPaintPending()) {
      window.__wfDeferEngine = false
      setGate(true)
      startEngine()
    }
    idle(prefetchOnPaint)
  }))
}

function watch(client: QueryClient, optionsFor: OptionsFor) {
  const pendingSaves = new Map<string, WeatherReport>()
  let saveScheduled = false
  const flushSaves = () => {
    saveScheduled = false
    const keep = savedKeys()
    for (const [key, report] of pendingSaves) if (keep.has(key)) void saveReport(key, report)
    pendingSaves.clear()
  }
  let prefetched = false
  const prefetchSaved = () => {
    if (prefetched || IS_MOCK || (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData) return
    prefetched = true
    setGate(true)
    void Promise.allSettled(allPlaces(useApp.getState()).map((p) =>
      client.prefetchQuery(optionsFor(p) as Parameters<QueryClient['prefetchQuery']>[0]),
    ))
  }

  prefetchOnPaint = prefetchSaved
  client.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.query.queryKey[0] !== 'weather') return
    const { query, action } = event
    const selected = hashKey(optionsFor(selectedPlace(useApp.getState())).queryKey) === query.queryHash
    if (action.type === 'error' && selected) setGate(true)
    if (action.type !== 'success') return
    const report = query.state.data as WeatherReport | undefined
    if (!report || seededReports.has(report)) return
    if (isEarlyReport(report)) {
      if (selected) mark('wf:report-early')
      return
    }
    if (selected) {
      const first = !marked.has('wf:report-full')
      mark('wf:report-full')
      // main.tsx registers the service worker after this (its precache waits for the forecast).
      if (first && typeof window !== 'undefined') window.dispatchEvent(new Event('wf:report-full'))
      setGate(true)
    }
    // Demo (?mock=) reports are synthetic: never persist them, or a later real visit would paint
    // them as saved live weather for the same place.
    if (IS_MOCK) return
    const [, lat, lon] = query.queryKey as [string, string, string]
    pendingSaves.set(`${lat},${lon}`, report)
    if (!saveScheduled) {
      saveScheduled = true
      idle(flushSaves)
    }
  })
}
